import React from 'react';
import classNames from 'classnames';
import { useFormState } from 'react-final-form';

import { useConfiguration } from '../../context/configurationContext';
import { intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import * as validators from '../../util/validators';
import {
  displayNameToUsername,
  isValidUsername,
  isReservedUsername,
} from '../../util/username';
import { checkUsernameAvailability } from '../../util/usernameAvailability';

import { FieldTextInput, IconSpinner } from '../../components';

import css from './UserFieldDisplayName.module.css';

const FIELD_NAME = 'displayName';

// Strip protocol + trailing slash so the preview reads "zuprize.no/u/…".
const toDisplayDomain = rootUrl => (rootUrl || '').replace(/^https?:\/\//, '').replace(/\/$/, '');

// Surface the field error as soon as the (debounced, async) check has
// settled, instead of waiting for a blur. The username is checked while
// the user types, so the "already taken" result should appear without
// needing to click out of the input. We only show it once the user has
// typed something and validation isn't in flight — passing it as
// FieldTextInput's `customErrorText` bypasses its touched gate.
const useEagerUsernameError = () => {
  const { values, errors, validating } = useFormState({
    subscription: { values: true, errors: true, validating: true },
  });
  const value = values?.[FIELD_NAME];
  const error = errors?.[FIELD_NAME];
  if (!value || !value.trim() || validating || !error) {
    return undefined;
  }
  return error;
};

/**
 * Live "Din profil-URL blir: zuprize.no/u/<slug>" line under the field.
 * Hidden while the async availability check runs (the field shows a
 * spinner then) and while there's a validation error (the field renders
 * that itself); shows the URL preview once the slug is valid + clear.
 */
const UsernamePreview = ({ rootUrl, intl }) => {
  const { values, errors, validating } = useFormState({
    subscription: { values: true, errors: true, validating: true },
  });
  const value = values?.[FIELD_NAME];
  const slug = displayNameToUsername(value || '');

  if (!value || !isValidUsername(slug) || isReservedUsername(slug)) {
    return null;
  }
  if (validating) {
    return null;
  }
  if (errors?.[FIELD_NAME]) {
    return null;
  }
  return (
    <p className={css.usernamePreview}>
      {intl.formatMessage(
        { id: 'UserFields.usernamePreview' },
        { url: `${toDisplayDomain(rootUrl)}/u/${slug}` }
      )}
    </p>
  );
};

/**
 * Display-name field. The display name doubles as the seller's public
 * handle, so we validate it as a vanity username: derive the slug, check
 * format/length + reserved words synchronously, then debounce-check
 * availability server-side. See util/username + util/usernameAvailability.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName
 * @param {string} props.className
 * @param {string} props.formId
 * @param {string} props.formName
 * @param {propTypes.userType} props.userTypeConfig
 * @param {intlShape} props.intl
 * @returns {JSX.Element}
 */
const UserFieldDisplayName = props => {
  const { rootClassName, className, formId, formName, userTypeConfig, intl } = props;
  const config = useConfiguration();

  const { displayInSignUp, required } = userTypeConfig?.displayNameSettings || {};
  const isDisabled = userTypeConfig?.defaultUserFields?.displayName === false;
  const isAllowedInSignUp = displayInSignUp === true;

  if (isDisabled || !isAllowedInSignUp) {
    return null;
  }

  const isRequired = required === true;

  // Async field validator: required → slug format/length → reserved →
  // availability (debounced). Fails open on a backend hiccup.
  const validate = async value => {
    if (!value || !value.trim()) {
      return isRequired
        ? intl.formatMessage({ id: `${formName}.displayNameRequired` })
        : undefined;
    }
    const slug = displayNameToUsername(value);
    if (!isValidUsername(slug)) {
      return intl.formatMessage({ id: 'UserFields.usernameInvalid' });
    }
    if (isReservedUsername(slug)) {
      return intl.formatMessage({ id: 'UserFields.usernameReserved' });
    }
    const { available, reason } = await checkUsernameAvailability(slug);
    if (!available) {
      if (reason === 'reserved') {
        return intl.formatMessage({ id: 'UserFields.usernameReserved' });
      }
      if (reason === 'invalid') {
        return intl.formatMessage({ id: 'UserFields.usernameInvalid' });
      }
      if (reason === 'taken') {
        return intl.formatMessage({ id: 'UserFields.usernameTaken' });
      }
      // check-unavailable / superseded → fail open (no error)
    }
    return undefined;
  };

  return (
    <UsernameField
      rootClassName={rootClassName}
      className={className}
      formId={formId}
      formName={formName}
      validate={validate}
      rootUrl={config.marketplaceRootURL}
      intl={intl}
    />
  );
};

// Field + live URL preview. Split out so it can subscribe to form state
// (useEagerUsernameError) without the parent's early `return null` making
// that a conditional hook.
const UsernameField = ({ rootClassName, className, formId, formName, validate, rootUrl, intl }) => {
  const customErrorText = useEagerUsernameError();
  const { validating } = useFormState({ subscription: { validating: true } });
  return (
    <div className={classNames(className, { [rootClassName]: !!rootClassName })}>
      <div className={css.fieldContainer}>
        <FieldTextInput
          type="text"
          id={formId ? `${formId}.displayName` : 'displayName'}
          name={FIELD_NAME}
          label={intl.formatMessage({ id: `${formName}.displayNameLabel` })}
          placeholder={intl.formatMessage({ id: `${formName}.displayNamePlaceholder` })}
          validate={validate}
          customErrorText={customErrorText}
        />
        {validating ? (
          <span className={css.spinner}>
            <IconSpinner rootClassName={css.spinnerIcon} />
          </span>
        ) : null}
      </div>
      <UsernamePreview rootUrl={rootUrl} intl={intl} />
    </div>
  );
};

export default UserFieldDisplayName;
